/**
 * When a lazy route chunk fails after deploy (404 / stale hash), we full-reload once
 * and restore the intended URL so the user does not need a second click.
 * Reload attempts are capped to avoid infinite loops.
 */
export const CHUNK_RECOVERY_STORAGE_KEY = 'rukh_chunk_recovery_fullpath'

export const CHUNK_RECOVERY_ATTEMPTS_KEY = 'rukh_chunk_recovery_attempts'

export const CHUNK_RECOVERY_PENDING_TOAST_KEY = 'rukh_chunk_recovery_pending_toast'

/** Max full-page recovery reloads within {@link CHUNK_RECOVERY_WINDOW_MS}. */
export const MAX_CHUNK_RECOVERY_RELOADS = 2

/** Sliding window for counting recovery reloads (ms). */
export const CHUNK_RECOVERY_WINDOW_MS = 120_000

export const CHUNK_RECOVERY_GIVE_UP_USER_MESSAGE =
  'This page could not be loaded after the last update. Try again from the menu or refresh the browser. You are still signed in.'

/** Dev-only query param to bust stale Vite pre-bundle module cache on recovery reload. */
export const CHUNK_RECOVERY_DEV_QUERY_PARAM = '_chunkRecovery'

/** Build a cache-busting URL for dev recovery (plain reload keeps stale `?v=` dep graph). */
export function buildDevChunkRecoveryUrl(href = window.location.href): string {
  const url = new URL(href)
  url.searchParams.set(CHUNK_RECOVERY_DEV_QUERY_PARAM, String(Date.now()))
  return url.toString()
}

/** Remove dev recovery query param from a path or full URL. */
export function stripChunkRecoveryQueryParam(fullPath: string): string {
  try {
    const url = new URL(fullPath, window.location.origin)
    url.searchParams.delete(CHUNK_RECOVERY_DEV_QUERY_PARAM)
    const next = url.pathname + url.search + url.hash
    return next || '/'
  } catch {
    return fullPath
  }
}

/** Drop dev recovery query param from the address bar without re-navigating. */
export function cleanupChunkRecoveryQueryParamFromAddressBar(): void {
  if (!import.meta.env.DEV) return
  const current = window.location.pathname + window.location.search + window.location.hash
  const cleaned = stripChunkRecoveryQueryParam(current)
  if (cleaned !== current) {
    window.history.replaceState(window.history.state, '', cleaned)
  }
}

/** Indirection so Vitest can mock reload without touching `window.location.reload`. */
export const chunkRecoveryReloadExecutor = {
  execute(): void {
    if (import.meta.env.DEV) {
      window.location.replace(buildDevChunkRecoveryUrl())
      return
    }
    window.location.reload()
  },
}

type RecoveryAttempts = { count: number; windowStart: number }

function readAttempts(): RecoveryAttempts | null {
  try {
    const raw = sessionStorage.getItem(CHUNK_RECOVERY_ATTEMPTS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as RecoveryAttempts
    if (
      typeof parsed?.count === 'number' &&
      typeof parsed?.windowStart === 'number' &&
      Number.isFinite(parsed.count) &&
      Number.isFinite(parsed.windowStart)
    ) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

function writeAttempts(meta: RecoveryAttempts): void {
  try {
    sessionStorage.setItem(CHUNK_RECOVERY_ATTEMPTS_KEY, JSON.stringify(meta))
  } catch {
    /* quota / private mode */
  }
}

function removeAttempts(): void {
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_ATTEMPTS_KEY)
  } catch {
    /* ignore */
  }
}

function removeRecoveryFullPath(): void {
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

function queueGiveUpToast(): void {
  try {
    sessionStorage.setItem(CHUNK_RECOVERY_PENDING_TOAST_KEY, CHUNK_RECOVERY_GIVE_UP_USER_MESSAGE)
  } catch {
    /* quota / private mode */
  }
}

/** Clears recovery attempt counter and any deferred toast (call after successful `router.replace` or normal boot). */
export function clearChunkRecoveryAttemptState(): void {
  removeAttempts()
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_PENDING_TOAST_KEY)
  } catch {
    /* ignore */
  }
}

/** Returns pending give-up message and removes the key. */
export function flushChunkRecoveryPendingToast(): string | null {
  try {
    const v = sessionStorage.getItem(CHUNK_RECOVERY_PENDING_TOAST_KEY)
    if (v) {
      sessionStorage.removeItem(CHUNK_RECOVERY_PENDING_TOAST_KEY)
    }
    return v
  } catch {
    return null
  }
}

/** Shows deferred give-up toast if one was queued (safe from router after mount or from main after mount). */
export function showChunkRecoveryPendingToastIfAny(): void {
  const msg = flushChunkRecoveryPendingToast()
  if (msg) {
    void import('vue-toastification')
      .then(({ useToast }) => {
        useToast().error(msg, { timeout: 8000 })
      })
      .catch(() => {})
  }
}

/** True when dynamic import failed (chunk 404, network, etc.). */
export function isChunkLoadError(error: unknown): boolean {
  const name = (error as Error)?.name ?? ''
  const msg = typeof (error as Error)?.message === 'string' ? (error as Error).message : String(error)
  const lower = msg.toLowerCase()
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    lower.includes('error loading dynamically imported module')
  )
}

export function consumeChunkRecoveryTarget(): string | null {
  try {
    const v = sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY)
    if (v) {
      sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY)
    }
    return v
  } catch {
    return null
  }
}

/**
 * Persists intended route and reloads so the next load can `router.replace` to it,
 * unless too many reloads occurred recently (then queues a toast and does not reload).
 * @returns `true` if a reload was triggered, `false` if the limit was reached (caller may run `showChunkRecoveryPendingToastIfAny` in a microtask).
 */
export function persistChunkRecoveryTargetAndReload(fullPath: string): boolean {
  const now = Date.now()
  let meta = readAttempts()

  if (!meta) {
    meta = { count: 0, windowStart: now }
  } else if (now - meta.windowStart > CHUNK_RECOVERY_WINDOW_MS) {
    meta = { count: 0, windowStart: now }
  }

  if (meta.count >= MAX_CHUNK_RECOVERY_RELOADS) {
    console.warn('[chunk] Recovery reload limit reached; not reloading again.')
    removeRecoveryFullPath()
    removeAttempts()
    queueGiveUpToast()
    return false
  }

  meta.count += 1
  if (meta.count === 1) {
    meta.windowStart = now
  }
  writeAttempts(meta)

  try {
    if (fullPath) {
      sessionStorage.setItem(CHUNK_RECOVERY_STORAGE_KEY, fullPath)
    }
  } catch {
    /* quota / private mode */
  }
  chunkRecoveryReloadExecutor.execute()
  return true
}
